import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {buildErrorMessage} from "../utils";
// noinspection TypeScriptPreferShortImport
import {NDropdownDirective, NDropdownMenuComponent} from "../dropdown/public_exports";
import {IControlWithErrors, NControlsValidationErrors, NInputErrorMessages, NOptionComponent} from "../public_components";
// noinspection TypeScriptPreferShortImport
import {ErrorMessagesFactory} from "../../services/error-messages-factory.service";
import {BehaviorSubject, combineLatest, Subscription} from "rxjs";
import {KeyCodes} from "../KeyCodes";
import {debounceTime, distinctUntilChanged, filter, map, share} from "rxjs/operators";

const FILTER_DEBOUNCE = 500;
const compareOptionValues = (a, b, trackBy) => {
  if (trackBy && a && b && a[trackBy] && b[trackBy]) {
    return a[trackBy] === b[trackBy];
  } else if (typeof a === "object" && typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
    }
  }

  return a === b;
};

@Component({
  selector: "n-select",
  templateUrl: "./n-select.html",
  styleUrls: [
    "./n-select.css"
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NSelectComponent),
      multi: true
    }
  ]
})
export class NSelectComponent implements ControlValueAccessor, OnInit, IControlWithErrors, OnDestroy {

  @Input() placeholder: string;
  @Input() isDisabled: boolean;
  @Input() trackBy = "id";
  @Input() renderInHost: boolean;

  @ViewChild("menu", {static: false}) menu: NDropdownMenuComponent;
  @ViewChild("selectButton", {static: false}) selectButton: ElementRef;
  @ViewChild(NDropdownDirective, {static: false}) dropdown: NDropdownDirective;
  @Output() opened = new EventEmitter<any>();
  selectedOptionId$ = new BehaviorSubject<any>(undefined);
  selectedOption$ = new BehaviorSubject<NOptionComponent>(undefined);
  optionChanged$ = new BehaviorSubject<NOptionComponent[]>(undefined);
  /**
   * Custom error message for input
   */
  @Input() errorMessages: NInputErrorMessages;
  @Input() required: boolean;
  errorMessage: string;
  isSelectActive = false;
  switcher: boolean;
  private filtering$ = new BehaviorSubject<string>("");
  private propagateChange: (_) => void;
  private touchedFn: () => void;
  private isDestroyed = false;
  private subscribes: Subscription[] = [];

  constructor(private el: ElementRef,
              private messageFactory: ErrorMessagesFactory,
              private cd: ChangeDetectorRef,
              @Attribute("wide") wide: string | null,
              @Attribute("switcher") switcher: string | null,
              @Attribute("fitWidth") fitWidth: string | null) {
    if (wide !== null) {
      this.el.nativeElement.classList.add("_wide");
    } else if (fitWidth !== null) {
      this.el.nativeElement.classList.add("_fit-width");
    }

    this.switcher = switcher !== null;

    if (this.switcher) {
      this.el.nativeElement.classList.add("_switcher");
    }

  }

  private _optionComps: QueryList<NOptionComponent>;

  get optionComps(): QueryList<NOptionComponent> {
    return this._optionComps;
  }

  @ContentChildren(forwardRef(() => NOptionComponent)) set optionComps(queryList: QueryList<NOptionComponent>) {
    this._optionComps = queryList;
    this._optionComps.changes.subscribe(() => {
      this.subscribeOnOptionChanges();
    });
    this.subscribeOnOptionChanges();
  }

  setErrors(errors: NControlsValidationErrors) {
    this.processErrors(errors);
  }

  onSelectButtonClick(e) {
    e.preventDefault();
    return false;
  }

  ngOnInit() {
    combineLatest(
      this.optionChanged$.pipe(filter(x => !!x)),
      this.selectedOptionId$.pipe(distinctUntilChanged())
    )
      .pipe(
        map(([opts, value]) => opts.find(opt => compareOptionValues(opt.value, value, this.trackBy))),
        share()
      ).subscribe(option => {
        this.selectedOption$.next(option);
      });

    this.processErrors(undefined);

    this.filtering$
      .subscribe(word => {
        if (word.length === 0) {
          return;
        }
        this.focusOrSelect(word);
      });

    this.filtering$
      .pipe(
        debounceTime(FILTER_DEBOUNCE)
      )
      .subscribe(word => {
        if (!word) {
          return;
        }
        // clear filter after some debounce
        this.filtering$.next("");
      });
  }

  optionIdentity(index: number, item: NOptionComponent) {
    return item.value && item.value[this.trackBy] || item.value || item;
  }

  selectOption(option: NOptionComponent) {
    if (this.isDestroyed || !option) {
      return;
    }

    this.selectedOptionId$.next(option.value);
    if (this.propagateChange && option) {
      this.propagateChange(option.value);
    }

    this.selectButton.nativeElement.focus();
  }

  onTouched() {
    if (this.touchedFn) {
      this.touchedFn();
    }
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.unsubscribeFromOptionChanges();
  }

  writeValue(val: any): void {
    this.selectedOptionId$.next(val);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.touchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cd.markForCheck();
  }

  onKeyDown(e: KeyboardEvent) {
    const key = e.key;
    if (key && key.length === 1) {
      // user typing, filter results
      let filterValue = this.filtering$.value;
      if (filterValue !== key) {
        // continue typing
        filterValue = filterValue + key;
      }
      this.filtering$.next(filterValue);
    } else if (!this.dropdown.opened) {

      const prevent = () => {
        event.preventDefault();
        event.stopPropagation();
      };

      const keyCode = e.which || e.keyCode || e.code;
      switch (keyCode) {
        case KeyCodes.ARROW_UP: {
          this.selectPrevOption();
          prevent();
          break;
        }
        case KeyCodes.ARROW_DOWN: {
          this.selectNextOption();
          prevent();
          break;
        }
        case KeyCodes.END: {
          this.selectLastOption();
          prevent();
          break;
        }
        case KeyCodes.HOME: {
          this.selectFirstOption();
          prevent();
          break;
        }
      }

    }
  }

  onMenuOpened() {
    this.isSelectActive = true;
    Promise.resolve().then(() => {
      // wait until popover will shown to scroll to focused item
      this.focusToCurrent();
    });
  }

  onMenuClosed() {
    this.isSelectActive = false;
    this.onTouched();
  }

  subscribeOnOptionChanges() {
    this.unsubscribeFromOptionChanges();
    this.subscribes = this.optionComps.map(item =>
      item.change.subscribe(() => {
        this.optionChanged$.next(this.optionComps.toArray());
      })
    );

    this.optionChanged$.next(this.optionComps.toArray());
  }

  unsubscribeFromOptionChanges() {
    this.subscribes.forEach(sub => sub.unsubscribe());
  }

  private processErrors(errors: NControlsValidationErrors) {
    if (!errors) {
      if (this.required) {
        this.errorMessage = buildErrorMessage({required: true}, this.messageFactory, this.errorMessages);
      } else {
        this.errorMessage = "";
      }
      return;
    }

    this.errorMessage = buildErrorMessage(errors, this.messageFactory, this.errorMessages);
  }

  private selectNextOption() {
    this.dropdown.nDropdown.focusNext();
    this.selectFocused();
  }

  private selectLastOption() {
    this.dropdown.nDropdown.focusLast();
    this.selectFocused();
  }

  private selectPrevOption() {
    this.dropdown.nDropdown.focusPrev();
    this.selectFocused();
  }

  private selectFirstOption() {
    this.dropdown.nDropdown.focusFirst();
    this.selectFocused();
  }

  private selectFocused() {
    if (!this.dropdown.opened) {
      this.dropdown.open();
    }
    this.selectOption(this.optionComps[this.dropdown.nDropdown.focusedIndexSnapshot]);
  }

  private focusOrSelect(key: string) {
    key = key.toUpperCase();

    const indexes: number[] = [];
    this.optionComps.forEach((option: NOptionComponent, index: number) => {
      if ((option.heading || "").toUpperCase().startsWith(key)) {
        indexes.push(index);
      }
    });

    if (indexes.length === 0) {
      // not found
      return;
    }
    const currentIndex = this.dropdown.nDropdown.focusedIndexSnapshot;
    let indexToFocus = indexes.find(index => index > currentIndex);
    if (indexToFocus === undefined) {
      indexToFocus = indexes[0];
    }

    if (indexToFocus !== currentIndex) {
      this.dropdown.nDropdown.setFocusedItemIndex(indexToFocus);
      if (!this.dropdown.opened) {
        this.selectFocused(); // select result if not opened dropdown
      }
    }
  }

  private focusToCurrent() {
    const index = this.optionComps
      .toArray()
      .findIndex(o => compareOptionValues(o.value, this.selectedOptionId$.value, this.trackBy));

    if (index !== -1) {
      this.dropdown.nDropdown.setFocusedItemIndex(index);
    }
  }
}
